import React, { useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Upload, Info, X } from 'lucide-react';
import { isDarkAtom } from '../../atoms/shared/uiAtoms';
import { SettingsPageLayout } from './components/layout/SettingsPageLayout';
import {
  createCharacterAtom,
  updateCharacterAtom,
  charactersErrorAtom,
} from '../../atoms/v3/characterAtoms';
import {
  enqueueFilesAtom,
  fileValuesSelector,
  removeSingleFileAtom,
} from '../../atoms/v3/uppyAtoms';
import { Character, CreateCharacterRequest, UpdateCharacterRequest } from '../../types/character';
import * as characterService from '../../services/characterService';

/**
 * 角色卡编辑器页面
 */
export const CharacterEditor: React.FC = () => {
  const { t } = useTranslation(['v3/character', 'common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [isDark] = useAtom(isDarkAtom);

  // 状态管理
  const [, createCharacter] = useAtom(createCharacterAtom);
  const [, updateCharacter] = useAtom(updateCharacterAtom);
  const [error] = useAtom(charactersErrorAtom);

  // 文件上传状态管理
  const [, enqueueFiles] = useAtom(enqueueFilesAtom);
  const [uploadedFiles] = useAtom(fileValuesSelector);
  const [, removeSingleFile] = useAtom(removeSingleFileAtom);

  // 表单状态
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    conversationPrompt: '',
    imageScenePrompt: '',
    imageKeywordsPositive: '',
    imageKeywordsNegative: '',
    avatarUrl: '',
    isPublic: true,
    allowPromptVisible: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [character, setCharacter] = useState<Character | null>(null);

  const isEditing = Boolean(id);

  // 加载现有角色卡数据（编辑模式）
  useEffect(() => {
    if (isEditing && id) {
      const loadCharacter = async () => {
        try {
          const characterData = await characterService.fetchCharacterById(id);
          setCharacter(characterData);
          setFormData({
            name: characterData.name,
            description: characterData.description,
            conversationPrompt: characterData.conversationPrompt,
            imageScenePrompt: characterData.imageScenePrompt || '',
            imageKeywordsPositive: characterData.imageKeywordsPositive || '',
            imageKeywordsNegative: characterData.imageKeywordsNegative || '',
            avatarUrl: characterData.avatarUrl || '',
            isPublic: characterData.type === 'public',
            allowPromptVisible: characterData.allowPromptVisible || false,
          });
        } catch (error) {
          console.error('Failed to load character:', error);
          setSubmitError(error instanceof Error ? error.message : 'Failed to load character');
        }
      };

      loadCharacter();
    }
  }, [isEditing, id]);

  // 处理表单字段变更
  const handleFieldChange = (field: keyof typeof formData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSubmitError(null); // 清除错误状态
  };

  // 处理表单提交
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 验证必填字段
    if (!formData.name.trim() || !formData.description.trim() || !formData.conversationPrompt.trim()) {
      setSubmitError(t('v3/character:requiredFieldsError', '请填写所有必填字段'));
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (isEditing && id) {
        // 更新角色卡
        const updateData: UpdateCharacterRequest = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          conversationPrompt: formData.conversationPrompt.trim(),
          imageScenePrompt: formData.imageScenePrompt.trim() || undefined,
          imageKeywordsPositive: formData.imageKeywordsPositive.trim() || undefined,
          imageKeywordsNegative: formData.imageKeywordsNegative.trim() || undefined,
          avatarUrl: formData.avatarUrl.trim() || undefined,
          isPublic: formData.isPublic,
          allowPromptVisible: formData.allowPromptVisible,
        };

        await updateCharacter(id, updateData);
      } else {
        // 创建角色卡
        const createData: CreateCharacterRequest = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          conversationPrompt: formData.conversationPrompt.trim(),
          imageScenePrompt: formData.imageScenePrompt.trim() || undefined,
          imageKeywordsPositive: formData.imageKeywordsPositive.trim() || undefined,
          imageKeywordsNegative: formData.imageKeywordsNegative.trim() || undefined,
          avatarUrl: formData.avatarUrl.trim() || undefined,
          isPublic: formData.isPublic,
          allowPromptVisible: formData.allowPromptVisible,
        };

        await createCharacter(createData);
      }

      // 成功后返回角色卡市场
      navigate('/v3/character-market', { replace: true });
    } catch (error) {
      console.error('Failed to save character:', error);
      setSubmitError(error instanceof Error ? error.message : 'Failed to save character');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 处理头像上传
  const handleAvatarUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        // 验证文件类型和大小
        if (!file.type.startsWith('image/')) {
          setSubmitError(t('v3/character:invalidFileType', '请选择图片文件'));
          return;
        }
        if (file.size > 5 * 1024 * 1024) { // 5MB限制
          setSubmitError(t('v3/character:fileTooLarge', '图片文件不能超过5MB'));
          return;
        }

        // 使用uppy上传文件
        enqueueFiles([{
          name: file.name,
          type: file.type,
          data: file,
          size: file.size,
        }]);
      }
    };
    input.click();
  };

  // 监听文件上传完成
  useEffect(() => {
    const completedFiles = uploadedFiles.filter(file =>
      file.uploadStatus === 'success' && file.finalUrl
    );

    if (completedFiles.length > 0) {
      const latestFile = completedFiles[completedFiles.length - 1];
      if (latestFile.finalUrl && latestFile.finalUrl !== formData.avatarUrl) {
        handleFieldChange('avatarUrl', latestFile.finalUrl);
        // 清理已完成的文件
        removeSingleFile(latestFile.id);
      }
    }
  }, [uploadedFiles, formData.avatarUrl, removeSingleFile]);

  // 获取当前上传中的头像文件
  const uploadingAvatarFile = uploadedFiles.find(file =>
    file.type.startsWith('image/') &&
    ['preparing', 'uploading'].includes(file.uploadStatus)
  );

  return (
    <SettingsPageLayout
      title={isEditing
        ? t('v3/character:editCharacter', '编辑角色卡')
        : t('v3/character:createCharacter', '创建角色卡')
      }
      onBack={() => navigate(-1)}
    >
      {/* 保存按钮 - 放在页面顶部 */}
      <div className="mb-6">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`
            w-full px-4 py-3 rounded-lg font-medium transition-colors
            ${isSubmitting
              ? isDark
                ? 'bg-ios-dark-gray4 text-ios-dark-tertiary-label'
                : 'bg-gray-300 text-gray-500'
              : isDark
                ? 'bg-ios-dark-blue text-white hover:bg-blue-600'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }
          `}
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
              <span>{t('common:saving', '保存中...')}</span>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              <Save size={16} />
              <span>{t('common:save', '保存')}</span>
            </div>
          )}
        </button>
      </div>

      {/* 表单内容 */}
      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl mx-auto">
        {/* 错误提示 */}
        {(submitError || error) && (
          <div className={`p-4 rounded-xl ${isDark ? 'bg-ios-dark-red text-white' : 'bg-red-50 text-red-800'}`}>
            {submitError || error}
          </div>
        )}

        {/* 状态提示 */}
        {character && (
          <div className={`p-4 rounded-xl ${isDark ? 'bg-ios-dark-gray5' : 'bg-blue-50'}`}>
            {character.type === 'private' ? (
              <div className="flex items-start space-x-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${isDark ? 'bg-ios-dark-blue' : 'bg-blue-500'}`}>
                  <span className="text-white text-xs">🔒</span>
                </div>
                <div>
                  <p className={`text-sm font-medium ${isDark ? 'text-ios-dark-label' : 'text-blue-900'}`}>
                    {t('v3/character:privateCharacter', '私密角色卡')}
                  </p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-ios-dark-secondary-label' : 'text-blue-700'}`}>
                    {t('v3/character:privateCharacterEditTip', '这是您的私密角色卡，编辑后立即生效')}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start space-x-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                  character.status === 'approved'
                    ? isDark ? 'bg-green-600' : 'bg-green-500'
                    : character.status === 'pending'
                    ? isDark ? 'bg-orange-600' : 'bg-orange-500'
                    : isDark ? 'bg-red-600' : 'bg-red-500'
                }`}>
                  <span className="text-white text-xs">🌐</span>
                </div>
                <div>
                  <p className={`text-sm font-medium ${isDark ? 'text-ios-dark-label' : 'text-blue-900'}`}>
                    {t('v3/character:publicCharacter', '公开角色卡')}
                    {character.status === 'approved' && (
                      <span className={`ml-2 text-xs ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                        ({t('v3/character:statusApproved', '已发布')})
                      </span>
                    )}
                    {character.status === 'pending' && (
                      <span className={`ml-2 text-xs ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                        ({t('v3/character:statusPending', '审核中')})
                      </span>
                    )}
                    {character.status === 'rejected' && (
                      <span className={`ml-2 text-xs ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                        ({t('v3/character:statusRejected', '审核失败')})
                      </span>
                    )}
                  </p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-ios-dark-secondary-label' : 'text-blue-700'}`}>
                    {character.status === 'approved'
                      ? t('v3/character:publicCharacterEditTip', '编辑后需要重新审核，审核通过前其他用户仍使用旧版本')
                      : character.status === 'pending'
                      ? t('v3/character:pendingCharacterEditTip', '这个公开角色卡正在审核中，您可以继续编辑但需要重新提交审核')
                      : t('v3/character:rejectedCharacterEditTip', '审核失败，修改后可以重新提交审核')
                    }
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 头像上传 */}
        <div className="space-y-3">
          <label className={`block text-sm font-medium ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'}`}>
            {t('v3/character:avatar', '头像')}
          </label>
          <div className="flex items-center space-x-4">
            <div className={`relative w-16 h-16 rounded-full flex items-center justify-center text-xl font-semibold ${isDark ? 'bg-ios-dark-gray4 text-ios-dark-label' : 'bg-gray-200 text-gray-700'}`}>
              {formData.avatarUrl ? (
                <>
                  <img
                    src={formData.avatarUrl}
                    alt="Avatar"
                    className="w-full h-full rounded-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => handleFieldChange('avatarUrl', '')}
                    className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${isDark ? 'bg-ios-dark-red text-white' : 'bg-red-500 text-white'} hover:opacity-80 transition-opacity`}
                  >
                    <X size={12} />
                  </button>
                </>
              ) : uploadingAvatarFile ? (
                <div className="w-full h-full rounded-full flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (
                formData.name.charAt(0).toUpperCase() || '?'
              )}
            </div>
            <div className="flex flex-col space-y-2">
              <button
                type="button"
                onClick={handleAvatarUpload}
                disabled={!!uploadingAvatarFile}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
                  uploadingAvatarFile
                    ? isDark
                      ? 'border-ios-dark-separator bg-ios-dark-gray4 text-ios-dark-tertiary-label cursor-not-allowed'
                      : 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                    : isDark
                      ? 'border-ios-dark-separator bg-ios-dark-gray5 hover:bg-ios-dark-gray4'
                      : 'border-ios-light-separator bg-white hover:bg-gray-50'
                }`}
              >
                <Upload size={16} />
                <span>
                  {uploadingAvatarFile
                    ? t('v3/character:uploading', '上传中...')
                    : t('v3/character:uploadAvatar', '上传头像')
                  }
                </span>
              </button>
              {uploadingAvatarFile && (
                <div className="text-xs text-center">
                  <div className={`w-full bg-gray-200 rounded-full h-1 ${isDark ? 'bg-ios-dark-gray4' : 'bg-gray-200'}`}>
                    <div
                      className={`h-1 rounded-full transition-all duration-300 ${isDark ? 'bg-ios-dark-blue' : 'bg-blue-500'}`}
                      style={{ width: `${uploadingAvatarFile.progress || 0}%` }}
                    ></div>
                  </div>
                  <span className={`${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
                    {Math.round(uploadingAvatarFile.progress || 0)}%
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 角色名称 */}
        <div className="space-y-3">
          <label className={`block text-sm font-medium ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'}`}>
            {t('v3/character:characterName', '角色名称')} *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            placeholder={t('v3/character:characterNamePlaceholder', '例如：智能助手小明')}
            className={`
              w-full px-4 py-3 rounded-xl border transition-colors
              ${isDark
                ? 'bg-ios-dark-gray5 border-ios-dark-separator text-ios-dark-label placeholder-ios-dark-tertiary-label focus:border-ios-dark-blue'
                : 'bg-white border-ios-light-separator text-ios-light-label placeholder-ios-light-tertiary-label focus:border-blue-500'
              }
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20
            `}
            maxLength={50}
          />
        </div>

        {/* 角色描述 */}
        <div className="space-y-3">
          <label className={`block text-sm font-medium ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'}`}>
            {t('v3/character:characterDescription', '角色描述')} *
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            placeholder={t('v3/character:characterDescriptionPlaceholder', '简单描述这个角色的特点和用途...')}
            rows={3}
            className={`
              w-full px-4 py-3 rounded-xl border transition-colors resize-none
              ${isDark
                ? 'bg-ios-dark-gray5 border-ios-dark-separator text-ios-dark-label placeholder-ios-dark-tertiary-label focus:border-ios-dark-blue'
                : 'bg-white border-ios-light-separator text-ios-light-label placeholder-ios-light-tertiary-label focus:border-blue-500'
              }
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20
            `}
            maxLength={200}
          />
          <div className={`text-xs ${isDark ? 'text-ios-dark-tertiary-label' : 'text-ios-light-tertiary-label'}`}>
            {t('v3/character:characterDescriptionHint', '此描述仅用于标注和市场展示，不会影响AI对话的实际行为')}
          </div>
        </div>

        {/* 对话提示词 */}
        <div className="space-y-3">
          <label className={`block text-sm font-medium ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'}`}>
            {t('v3/character:conversationPrompt', '对话提示词')} *
          </label>
          <div className="relative">
            <textarea
              value={formData.conversationPrompt}
              onChange={(e) => handleFieldChange('conversationPrompt', e.target.value)}
              placeholder={t('v3/character:conversationPromptPlaceholder', '你是一个专业的AI助手，具有以下特点：\n1. 语气友善、专业\n2. 回答准确、有条理\n3. 能够理解用户需求并提供有价值的建议\n\n请根据这些特点来回复用户的问题。')}
              rows={8}
              className={`
                w-full px-4 py-3 rounded-xl border transition-colors resize-none
                ${isDark
                  ? 'bg-ios-dark-gray5 border-ios-dark-separator text-ios-dark-label placeholder-ios-dark-tertiary-label focus:border-ios-dark-blue'
                  : 'bg-white border-ios-light-separator text-ios-light-label placeholder-ios-light-tertiary-label focus:border-blue-500'
                }
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20
              `}
              maxLength={10000}
            />
            <div className={`flex justify-between items-center mt-2 text-xs ${isDark ? 'text-ios-dark-tertiary-label' : 'text-ios-light-tertiary-label'}`}>
              <span>{t('v3/character:conversationPromptHint', '详细的角色设定有助于AI更好地理解和扮演角色')}</span>
              <span className={`${formData.conversationPrompt.length > 9000 ? (isDark ? 'text-ios-dark-red' : 'text-red-500') : ''}`}>
                {formData.conversationPrompt.length}/10,000
              </span>
            </div>
          </div>
        </div>

        {/* 分享设置 */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className={`text-sm font-medium ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'}`}>
              {t('v3/character:shareToMarket', '分享到市场给其他用户使用')}
            </label>
            <button
              type="button"
              onClick={() => handleFieldChange('isPublic', !formData.isPublic)}
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                ${formData.isPublic
                  ? isDark ? 'bg-ios-dark-blue' : 'bg-blue-500'
                  : isDark ? 'bg-ios-dark-gray4' : 'bg-gray-300'
                }
              `}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                  ${formData.isPublic ? 'translate-x-6' : 'translate-x-1'}
                `}
              />
            </button>
          </div>

          {formData.isPublic && (
            <div className="space-y-3">
              <div className={`flex items-start space-x-2 p-3 rounded-lg ${isDark ? 'bg-ios-dark-gray5' : 'bg-blue-50'}`}>
                <Info size={16} className={`mt-0.5 ${isDark ? 'text-ios-dark-blue' : 'text-blue-500'}`} />
                <p className={`text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-blue-700'}`}>
                  {t('v3/character:publicCharacterNotice', '公开的角色卡将被提交自动审核后才会出现在市场，请勿提交违反公序良俗、色情暴力的角色卡')}
                </p>
              </div>

              {/* 提示词可见性控制 */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className={`text-sm font-medium ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'}`}>
                    {t('v3/character:allowPromptVisible', '允许其他用户查看完整提示词')}
                  </label>
                  <p className={`text-xs mt-1 ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
                    {t('v3/character:allowPromptVisibleDescription', '关闭后其他用户只能看到角色卡名称和描述，无法查看具体的对话提示词和图像提示词')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleFieldChange('allowPromptVisible', !formData.allowPromptVisible)}
                  className={`
                    relative inline-flex h-6 w-11 items-center rounded-full transition-colors ml-3
                    ${formData.allowPromptVisible
                      ? isDark ? 'bg-ios-dark-blue' : 'bg-blue-500'
                      : isDark ? 'bg-ios-dark-gray4' : 'bg-gray-300'
                    }
                  `}
                >
                  <span
                    className={`
                      inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                      ${formData.allowPromptVisible ? 'translate-x-6' : 'translate-x-1'}
                    `}
                  />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 朗读声音说明 */}
        <div className={`p-4 rounded-xl ${isDark ? 'bg-ios-dark-gray5' : 'bg-gray-50'}`}>
          <div className="flex items-center space-x-2 mb-2">
            <Info size={16} className={isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'} />
            <span className={`text-sm font-medium ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'}`}>
              {t('v3/character:voiceCustomization', '朗读声音自定义')}
            </span>
          </div>
          <p className={`text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
            {t('v3/character:voiceCustomizationComingSoon', '正在开发中，敬请期待')}
          </p>
        </div>
      </form>
    </SettingsPageLayout>
  );
};
